import { useI18n } from '@/hooks/useI18n';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateAccount } from '@/hooks/mutations/useAccountMutations';
import { useIdentities } from '@/hooks/queries/useIdentities';

const schema = z.object({
  username: z
    .string()
    .min(1, 'required')
    .regex(/^[a-zA-Z0-9._]+$/, 'invalidCharacters'),
  identityId: z.string().optional(),
});

type Values = z.infer<typeof schema>;

export function CreateAccountDialog({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const identities = useIdentities();
  const create = useCreateAccount();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
  });

  const identityId = watch('identityId');

  const onSubmit = (v: Values) => {
    create.mutate(
      {
        username: v.username,
        identityId: v.identityId || null,
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Ajouter un compte', 'Add an account')}</DialogTitle>
          <DialogDescription>
            {t(
              'Saisissez le @username Instagram et optionnellement son identité.',
              'Enter the Instagram @username and optionally its identity.',
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Field label={t('Nom d’utilisateur', 'Username')} error={errors.username?.message}>
            <Input
              autoFocus
              placeholder={t('username (sans @)', 'username (without @)')}
              {...register('username')}
            />
          </Field>
          <Field label={t('Identité', 'Identity')}>
            <Select
              value={identityId ?? ''}
              onValueChange={(v) => setValue('identityId', v === 'none' ? undefined : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('— aucune —', '— none —')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('— aucune —', '— none —')}</SelectItem>
                {identities.data?.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t('Annuler', 'Cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              {t('Créer', 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  const { t } = useI18n();
  const errorLabel =
    error === 'required'
      ? t('Requis', 'Required')
      : error === 'invalidCharacters'
        ? t('Caractères invalides', 'Invalid characters')
        : error;
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-medium text-text-2">{label}</div>
      {children}
      {errorLabel && <p className="text-[11px] text-rose">{errorLabel}</p>}
    </div>
  );
}
